/**
 * O catálogo de planos da gestão (#766, fase 2 da #765): o que cada plano
 * inclui e o preço PADRÃO dele. Só o Super Admin com a concessão em vigor chama
 * estas rotas.
 *
 * O preço do plano é copiado para o contrato da organização no dia da
 * contratação; mudar o plano depois não muda quem já contratou. Plano não se
 * apaga: desativado, some da criação, e quem já tem continua com ele.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

/** O maior número de usuários ou de GB que o banco guarda (`integer`). */
const MAIOR_INTEIRO = 2_147_483_647;

/**
 * Quantos bytes tem um GB de armazenamento contratado (#716): a régua binária,
 * a mesma do limite de tamanho dos anexos (1 MB = 1024 × 1024). A cota de 10 GB
 * vale 10 × 1024³ bytes.
 */
export const BYTES_POR_GB = 1024 * 1024 * 1024;

/** Dinheiro em micros (um milionésimo da moeda), como o resto do contrato. */
export const DinheiroDoPlanoSchema = z
  .object({ quantia_micros: z.number().int(), moeda: z.string() })
  .strict();

export type DinheiroDoPlano = z.infer<typeof DinheiroDoPlanoSchema>;

/** O dinheiro que a tela manda: sempre em reais, e nunca negativo. */
const DinheiroInformadoSchema = z
  .object({ quantia_micros: z.number().int().nonnegative(), moeda: z.literal('BRL') })
  .strict();

export const PlanoSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    /** Inativo não aparece para organização nova; quem já tem continua com ele. */
    ativo: z.boolean(),
    /**
     * Os preços e os usuários incluídos são nulos só no "Padrão", o plano
     * legado das organizações de antes do catálogo: cada uma ficou com os
     * usuários que usava, e o preço não foi definido.
     */
    mensalidade: DinheiroDoPlanoSchema.nullable(),
    usuarios_incluidos: z.number().int().nullable(),
    armazenamento_gb: z.number().int(),
    usuario_adicional: DinheiroDoPlanoSchema.nullable(),
    gb_adicional: DinheiroDoPlanoSchema.nullable(),
    observacao: z.string().nullable(),
    /** Quantas organizações não encerradas têm este plano hoje. */
    organizacoes: z.number().int(),
    atualizado_em: InstanteSchema,
  })
  .strict();

export type Plano = z.infer<typeof PlanoSchema>;

export const PlanosSchema = z.object({ itens: z.array(PlanoSchema) }).strict();

export type Planos = z.infer<typeof PlanosSchema>;

/** Plano novo: nasce ativo, com os usuários, o armazenamento e os preços. */
export const CriarPlanoSchema = z
  .object({
    nome: z.string().trim().min(1).max(80),
    mensalidade: DinheiroInformadoSchema,
    usuarios_incluidos: z.number().int().min(1).max(MAIOR_INTEIRO),
    armazenamento_gb: z.number().int().min(1).max(MAIOR_INTEIRO),
    usuario_adicional: DinheiroInformadoSchema,
    gb_adicional: DinheiroInformadoSchema,
    observacao: z.string().trim().max(280).nullable().optional(),
  })
  .strict();

export type CriarPlano = z.infer<typeof CriarPlanoSchema>;

/**
 * Editar ou desativar: só o que vier muda. Nenhum valor volta a nulo; o plano
 * legado ganha preço, mas nenhum plano perde o que tem.
 */
export const EditarPlanoSchema = z
  .object({
    nome: z.string().trim().min(1).max(80).optional(),
    ativo: z.boolean().optional(),
    mensalidade: DinheiroInformadoSchema.optional(),
    usuarios_incluidos: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    armazenamento_gb: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    usuario_adicional: DinheiroInformadoSchema.optional(),
    gb_adicional: DinheiroInformadoSchema.optional(),
    observacao: z.string().trim().max(280).nullable().optional(),
  })
  .strict()
  .refine((mudanca) => Object.keys(mudanca).length > 0, {
    message: 'Diga o que muda no plano.',
  });

export type EditarPlano = z.infer<typeof EditarPlanoSchema>;
